import React, { useCallback, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  ActionList,
  ActionListChildren,
  Badge,
  Box,
  Button,
  Chip,
  CopyButton,
  Dialog,
  DropdownMenu,
  DropdownMenuItem,
  Icon,
  Illustration,
  SidePanel,
  toast,
  useSidePanel,
} from '@node-space/ui-components'
import { appBannersId } from 'components/app/AppBanners'
import { DurationInMilliseconds } from 'constants/General'
import { useDeleteNotificationDestinationMutation } from 'hooks/mutations/useDeleteNotificationDestinationMutation'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { highlightRowBasedOnId } from 'utils/table/table'
import { NotificationDestination } from '../types'
import DestinationSidePanelHeader from './DestinationSidePanelHeader'

interface DestinationsListProps {
  destinations: NotificationDestination[]
  onEditDestination: (destination: NotificationDestination) => void
}

export const DestinationsList = ({ destinations, onEditDestination }: DestinationsListProps) => {
  const { t } = useTranslation()
  const bannerSpacing = useComponentHeight(appBannersId)

  const queryClient = useQueryClient()

  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false)
  const [deleteDestination, setDeleteDestination] = useState<NotificationDestination>()

  const handleOpenDeleteDialog = () => {
    setIsAlertDialogOpen(true)
  }

  const handleCloseDeleteDialog = () => {
    setIsAlertDialogOpen(false)
  }

  const { mutate: deleteWebHookDestination, isPending: isDeleteWebHookDestinationLoading } =
    useDeleteNotificationDestinationMutation()

  const onDeleteItem = destinationId => {
    const payload = {
      destinationId,
    }
    deleteWebHookDestination(payload, {
      onSuccess: () => {
        handleCloseDeleteDialog()
        setDeleteDestination(undefined)
        queryClient.invalidateQueries({ queryKey: ['destinations'] })
        toast({
          header: t('integrations.removeDestination'),
          variant: 'success',
        })
      },
      onError: () => {
        toast({
          header: t('oopsSomethingWentWrong'),
          variant: 'error',
          duration: DurationInMilliseconds.TEN_SECONDS,
        })
      },
    })
  }

  const {
    isSidePanelOpen: isDestinationOpen,
    sidePanelData: destinationData,
    paging: pagingDestinationList,
    setIsSidePanelOpen: setIsDestinationOpen,
    updateSidePanel: updateDestination,
  } = useSidePanel(destinations)

  const events = destinationData?.eventCodes

  const onDetailsClick = (index: number) => {
    updateDestination(index)
    setIsDestinationOpen(true)
  }

  const editDestination = useCallback(
    (destination: NotificationDestination) => {
      onEditDestination(destination)
    },
    [onEditDestination]
  )

  const actionListItems: ActionListChildren[] = useMemo(() => {
    return destinations?.map((destination, index) => {
      return {
        transparent: true,
        id: destination?.id,
        key: destination?.id,
        testId: destination?.id,
        border: false,
        title: destination?.description ?? destination?.url,
        Avatar: <Icon name="Globe" />,
        onItemClick: () => onDetailsClick(index),
        CustomAction: (
          <DropdownMenu
            DropdownMenuItems={[
              <DropdownMenuItem
                key={`action-info-${destination?.id}`}
                LeadingIcon={<Icon name="Info" />}
                onClick={() => onDetailsClick(index)}
              >
                {t('integrations.details')}
              </DropdownMenuItem>,
              <DropdownMenuItem
                key={`action-edit-${destination?.id}`}
                LeadingIcon={<Icon name="SimplePencilCustom" />}
                onClick={() => editDestination(destination)}
              >
                {t('integrations.edit')}
              </DropdownMenuItem>,
              <DropdownMenuItem
                key={`action-delete-${destination?.id}`}
                LeadingIcon={<Icon name="Trash" />}
                onClick={() => {
                  setDeleteDestination(destination)
                  handleOpenDeleteDialog()
                }}
              >
                {t('integrations.delete')}
              </DropdownMenuItem>,
            ]}
          >
            <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
          </DropdownMenu>
        ),
        ActionButton: (
          <CopyButton copyText={destination?.url}>{t('integrations.copyURL')}</CopyButton>
        ),
      }
    })
  }, [destinations, t, destinationData?.id, onDetailsClick, handleOpenDeleteDialog])

  return (
    <>
      <ActionList id="destinations-action-list" items={actionListItems} />
      <SidePanel
        id="side-panel-destination"
        isOpen={isDestinationOpen}
        onClose={() => setIsDestinationOpen(false)}
        paging={pagingDestinationList}
        activeRowId={destinationData?.id}
        onActiveRowChange={highlightRowBasedOnId}
        topOffset={bannerSpacing}
        header={<DestinationSidePanelHeader destinationData={destinationData} />}
        body={
          <Box flex flexCol gapY={12} width="full">
            <>
              <Box flex align="center" gap={8}>
                <h4 className="text-h4 text-foreground-prominent">{t('integrations.events')}</h4>
                <Badge>{events?.length}</Badge>
              </Box>
              <Box width="full" flex isFlexWrap gap={8}>
                {events?.map?.(event => (
                  <Chip
                    key={event}
                    variant="input"
                    label={event}
                    dismissible={false}
                    name={event}
                  />
                ))}
              </Box>
            </>
          </Box>
        }
      />

      <Dialog
        open={isAlertDialogOpen}
        showClose={false}
        onClose={handleCloseDeleteDialog}
        title={t('integrations.alertDialogTitle', {
          name: deleteDestination?.description,
        })}
        Illustration={<Illustration name="CloudLightning" />}
        ActionButtons={[
          <Button
            key="delete"
            variant="destructive"
            onClick={() => onDeleteItem(deleteDestination?.id)}
            loading={isDeleteWebHookDestinationLoading}
          >
            {t('integrations.delete')}
          </Button>,
          <Button key="cancel" onClick={handleCloseDeleteDialog} variant="secondary">
            {t('cancel')}
          </Button>,
        ]}
      >
        <Box flex justify="center">
          <p className="text-body-regular text-foreground-prominent text-center">
            {t('integrations.alertDialogDescription')}
          </p>
        </Box>
      </Dialog>
    </>
  )
}
